import { getEnv, types, hasEnv } from "mobx-state-tree";
import { getUrlFromRelativeOrFullString } from "../../utilities/url-utils";
import { stripPTNumberFromBranch } from "../../utilities/branch-utils";
import { parseUrl } from "query-string";
export const CurriculumConfig = types
    .model("CurriculumConfig", {
    // base URL of external curriculum unit repository
    curriculumSiteUrl: types.string,
    // unit code overrides (legacy unit code support)
    unitCodeMap: types.map(types.string),
    // default problem to load if none specified
    defaultUnit: "",
})
    .views(self => ({
    get env() {
        return hasEnv(self) ? getEnv(self) : undefined;
    }
}))
    .views(self => ({
    get curriculumBaseUrl() {
        var _a;
        const { curriculumBranch } = ((_a = self.env) === null || _a === void 0 ? void 0 : _a.urlParams) || {};
        const branchName = stripPTNumberFromBranch(curriculumBranch !== null && curriculumBranch !== void 0 ? curriculumBranch : "main");
        return `${self.curriculumSiteUrl}/branch/${branchName}`;
    }
}))
    .views(self => ({
    getUnitUrl(unitParam) {
        const unitParamUrl = getUrlFromRelativeOrFullString(unitParam);
        if (unitParamUrl) {
            return unitParamUrl.href;
        }
        const unitCode = self.unitCodeMap.get(unitParam) || unitParam;
        return `${self.curriculumBaseUrl}/${unitCode}/content.json`;
    }
}))
    .views(self => ({
    getUnit(unitParam) {
        const unitUrl = self.getUnitUrl(unitParam);
        const teacherGuideUrl = unitUrl.replace(/content\.json$/, "teacher-guide/content.json");
        return { "content": unitUrl, "guide": teacherGuideUrl };
    }
}))
    .views(self => ({
    // TODO: figure out why this logic is needed.
    getUnitBasePath(unitId) {
        const unitSpec = self.getUnit(unitId);
        if (!unitSpec)
            return "";
        return `${unitId}`;
    },
    getUnitSpec(unitId) {
        const requestedUnit = unitId ? self.getUnit(unitId) : undefined;
        return requestedUnit || (self.defaultUnit ? self.getUnit(self.defaultUnit) : undefined);
    },
    /**
     * Find the unit code from the passed in URL.
     * So "https://collaborative-learning.concord.org/branch/master/index.html?unit=s%2Bs
     * will return sas
     *
     * @param url
     * @returns
     */
    getUnitCode(url) {
        const queryParams = parseUrl(url);
        const unitCode = queryParams.query.unit
            ? queryParams.query.unit
            : undefined;
        const mappedUnitCode = unitCode
            ? self.unitCodeMap.get(unitCode)
            : undefined;
        return mappedUnitCode || unitCode;
    },
    /**
     * Returns the unit code, plus all the unit code variants for renamed unit codes.
     * So "sas" will return ["sas", "s s", "s+s", "stretching-and-shrinking"]
     */
    getUnitCodeVariants(unitId) {
        const variantsSet = new Set([unitId]);
        self.unitCodeMap.forEach((value, _key) => {
            const key = String(_key);
            if (value === unitId) {
                variantsSet.add(key);
            }
        });
        return Array.from(variantsSet);
    }
}));
/**
 * Find the problem ordinal from a passed in URL.
 * So "https://collaborative-learning.concord.org/branch/master/index.html?problem=3.1"
 * returns "3.1"
 *
 * @param url
 * @returns
 */
export function getProblemOrdinal(url) {
    const queryParams = parseUrl(url);
    return queryParams.query.problem
        ? queryParams.query.problem
        : undefined;
}
//# sourceMappingURL=data:application/json;base64,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